import { useQuery } from '@tanstack/react-query';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { listTransactions } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useActiveBranch } from '../lib/use-branch';
import { posStatusVariant } from '../lib/pos-status';
import { BranchPicker } from '../components/branch-picker';

/**
 * Time Logs (feature 14) — the cashier's own transactions for today. There is
 * no separate clock: the first and last transaction of the day stand in for
 * Time In / Time Out, with the full list below.
 */
export function TimeLogsPage() {
  const user = useAuthStore((s) => s.user);
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  const query = useQuery({
    queryKey: ['pos-my-day', branchId, user?.id, start.toDateString()],
    enabled: !!branchId && !!user?.id,
    queryFn: () =>
      listTransactions({
        branchId: branchId!,
        cashierId: user!.id,
        from: start.toISOString(),
        to: end.toISOString(),
        limit: 500,
      }),
  });

  const rows = query.data ?? [];
  // Rows come back newest-first; first/last of the day are the ends.
  const timeOut = rows.length ? rows[0]!.createdAt : null;
  const timeIn = rows.length ? rows[rows.length - 1]!.createdAt : null;
  const cashTotal = rows
    .filter((r) => r.paymentMethod === 'cash' && r.status === 'completed')
    .reduce((acc, r) => acc + Number(r.total), 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          Today&rsquo;s activity{' '}
          <span className="text-sm font-normal text-muted-foreground">{user?.email}</span>
        </CardTitle>
        <BranchPicker branches={branches} branchId={branchId} isScoped={isScoped} onChange={setSelectedBranchId} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Time in" value={timeIn ? new Date(timeIn).toLocaleTimeString() : '—'} />
          <Stat label="Time out" value={timeOut ? new Date(timeOut).toLocaleTimeString() : '—'} />
          <Stat label="Sales orders" value={String(rows.length)} />
          <Stat label="Cash sales" value={`₱${cashTotal.toFixed(2)}`} />
        </div>

        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !rows.length ? (
          <p className="text-sm text-muted-foreground">No transactions yet today.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="px-2 py-1.5">Time</th>
                <th className="px-2 py-1.5">Order</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5">Payment</th>
                <th className="px-2 py-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.orderId} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">
                    {new Date(t.createdAt).toLocaleTimeString()}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">{t.code}</td>
                  <td className="px-2 py-1.5">
                    <Badge variant={posStatusVariant(t.status)}>{t.status}</Badge>
                  </td>
                  <td className="px-2 py-1.5 text-xs">{t.paymentMethod ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">₱{Number(t.total).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
